<template>
  <div class="column-design-inner">
    <div class="design-title d-flex align-items-center">
      <span class="select-all">{{ item.name }}</span>
      <i class="ti ti-printer mr-1 print-content" @click.prevent="handlePrint"></i>
    </div>
    <div class="design-gallery mr-3 d-flex flex-column">
      <div class="design-thumbnail" :data-id="item.id" @click="handleDesignClick">
        <Image :src="$util.addQueryParam(item?.thumbnail, { size: 'xs' })" />
      </div>
      <div v-if="item.gallery?.length > 0" class="gallery d-flex flex-wrap">
        <div v-for="(image, index) in item.gallery.slice(0, 3)" :key="index" class="gallery-item thumbnail" @click="handlePreviewClick">
          <Image :src="$util.addQueryParam(image, { size: 'thumbnail' })" v-if="index < 2 || item.gallery.length <= 3" />
          <div v-else class="more-thumbnail">+{{ item.gallery.length - 2 }}</div>
        </div>
      </div>
    </div>
    <div v-if="settings?.more.indexOf('blueprint') != -1 && item?.blueprint" class="design-thumbnail mr-3" :data-id="item.id">
      <Image :src="$util.addQueryParam(item?.blueprint, { size: 'xs' })" />
    </div>
    <div class="design-main">
      <div class="design-summary">
        <div class="design-shop-name">
          <label>Shop Name: </label>
          <span class="select-all">{{ item.account_name }}</span>
        </div>
        <div class="design-seller">
          <label>Seller: </label>
          <span class="select-all">{{ item.user_name || '-' }}</span>
        </div>
        <div class="designer">
          <label>Designer: </label>
          <span class="select-all">{{ item.designer_name || '-' }}</span>
        </div>
        <div class="inspector">
          <label>Inspector: </label>
          <span class="select-all">{{ item.users['design-review']?.user_name || '-' }}</span>
        </div>
        <div class="mapper">
          <label>Mapper: </label>
          <span class="select-all">{{ item.users?.mapping?.user_name || '-' }}</span>
        </div>
        <div class="quantity">
          <label>Sku: </label>
          <span class="select-all">{{ item.sku || '-' }}</span>
        </div>
        <div class="quantity">
          <label>Quantity: </label>
          <span class="select-all" :class="{ 'text-danger font-weight-bold': item.quantity > 1 }">{{ item.quantity || '-' }}</span>
        </div>
        <div v-if="parseInt(item.deadline) > 0" class="deadline">
          <label>Deadline: </label>
          <span v-html="item.users?.confirm?.deadline ? this.$util.timestampToDateTime(parseInt(item.users.confirm.deadline)) : '-'" class="select-all"></span>
        </div>
        <div :class="`design-status flag ${item.status} ${item.flag}`">
          <label>Status: </label>
          <span>{{ listStatus[item.status]?.text || item.status }}</span>
        </div>
        <div class="manual-wrapper pt-2">
          <span class="manual ml-0">{{ item.manual ? 'Manual' : '' }}</span>
        </div>
        <div class="print-method">
          <label>Print Method: </label>
          <span class="select-all">{{ item.print_method || '-' }}</span>
        </div>
        <div class="color-batch">
          <label>Color Batch: </label>
          <span class="select-all">{{ item.color_batch || '-' }}</span>
        </div>

        <div v-if="item.refer_orders" class="refer-order pt-2">
          <label>Order: </label>
          <span v-for="(order, index) in item.refer_orders" :key="order.id">
            <a :href="order.href" target="_blank">#{{ order.external_id }}</a>
            <span v-if="index < item.refer_orders.length - 1">, </span>
          </span>
        </div>
      </div>
      <div class="design-meta">
        <template v-if="$_.isObjectLike(item.attributes)">
          <div v-for="(attribute, index) in item.attributes" :key="index" :class="`meta-group ${index}`">
            <label class="text-capitalize" :data-attr="index">{{ listAttributes[index]?.label || index }}: </label>
            <span class="select-all">{{ Object.values(attribute).join(', ') }}</span>
          </div>
        </template>
        <div v-if="item.note.length > 0" class="meta-group note">
          <label>Note: </label>
          <ul>
            <template v-for="(note, index) in item.note" :key="index">
              <li v-if="String(note).length > 0" v-html="renderNote(note)"></li>
            </template>
          </ul>
        </div>
      </div>
      <div v-if="item.vendor_mapping" class="design-vendor-mapping">
        <div class="vendor-mapping-inner">
          <div class="vendor-mapping-group account-name">
            <label>Account: </label>
            <span>{{ item.vendor_mapping.vendor_name }}</span>
          </div>
          <div class="vendor-mapping-group account-server">
            <label>Platform: </label>
            <span>{{ item.vendor_mapping.platform }}</span>
          </div>
          <div v-if="item.vendor_mapping?.id" class="vendor-mapping-group account-external-id">
            <label>Id: </label>
            <a v-if="item.vendor_mapping.url" :href="item.vendor_mapping.url" target="_black">{{ item.vendor_mapping.id }}</a>
            <span v-else>{{ item.vendor_mapping.id }}</span>
          </div>
          <div v-if="item.vendor_mapping?.sku" class="vendor-mapping-group account-external-id">
            <label>Sku: </label>
            <span>{{ item.vendor_mapping.sku }}</span>
          </div>
        </div>
      </div>
    </div>
    <div class="item-action">
      <i class="ti ti-dots-vertical"></i>
      <div class="list-action break-normal">
        <span v-for="(action, key, index) in item.more_actions" :key="index" :class="[action.id.replace('.', '-'), action.style || '']">
          <a href="#" @click.prevent="handleAction(product, action)">
            {{ action.label }}
          </a>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import BodyItemRowAction from '@/components/TableList/Cols/BodyItemRowAction.vue'
import print from '@/helper/print.js'
export default {
  extends: BodyItemRowAction,
  computed: {
    store() {
      return this.config?.store
    },
    more() {
      return this.config?.more
    },
    item() {
      return this.row?.design || {}
    },
    listStatus() {
      return this.store.getter('extra_attributes.status', {})
    },
    listAttributes() {
      return this.store.getter('extra_attributes.attributes', {})
    },
    settings() {
      return this.store.getter('extra_attributes.settings.tablelist', {})
    }
  },
  methods: {
    renderNote(str) {
      let links = this.$util.getLink(str)
      if (links !== null) {
        links.forEach((link) => {
          if (Object.keys(this.$util.options.extension.image).includes(this.$util.getExtensionFromString(link))) {
            str = str.replace(link, `<div class="attached preview d-flex"><img src="${link}" loading="lazy"><a href="${link}" target="_blank">${link}</a></div>`)
          }
        })
      }
      return str
    },
    handlePrint() {
      let order = _.cloneDeep(this.item.refer_orders.at(-1))
      order.line_items = _.filter(order.line_items, (item) => {
        return this.item.id == item.design_id
      })
      print.order(order)
    },
    handleDesignClick() {
      this.$emitter.emit('Modal', {
        name: 'design.view',
        data: this.row,
        $el: this.$el instanceof HTMLElement ? this.$el.closest('tr.item') : this.$el.parentNode.closest('tr.item')
      })
    },
    handlePreviewClick() {
      this.$emitter.emit('SubModal', {
        name: 'full-screen-image-preview',
        data: {
          files: Object.values(this.row.design.gallery),
          config: {
            thumbnail: true,
            background: true
          }
        }
      })
    }
  }
}
</script>
<style lang="scss">
.table-list.design {
  .attached.preview {
    padding-top: 5px;
    img {
      width: 60px;
      height: 60px;
      min-width: 60px;
      min-height: 60px;
      object-fit: contain;
      border: 1px solid #ccc;
      border-radius: var(--border-radius, 0.25rem);
    }
    a {
      padding-left: 10px;
    }
  }
  .reference {
    margin-top: 5px;
    a {
      height: auto;
      width: auto;
      font-size: 11px;
      cursor: pointer;
      padding: 2px 10px;
      margin: 0 5px 5px 0;
      border-radius: var(--border-radius, 0.25rem);
      display: inline-block;
      color: #333;
      background: #e4e4e4;
    }
  }
  .print-content {
    cursor: pointer;
    font-size: 16px;
    margin: 0 5px;
    margin-top: -1px;
    display: none;
  }
  tr:hover {
    .print-content {
      display: inline-block;
    }
  }
  .design-thumbnail {
    cursor: pointer;
  }
  .design-gallery {
    width: 200px;
    .gallery {
      margin: -5px;
      margin-top: 5px;
    }
    .gallery-item {
      width: 60px;
      height: 60px;
      margin: 5px;
      cursor: pointer;
      img {
        width: 100%;
        height: 100%;
        -o-object-fit: cover;
        object-fit: cover;
      }
    }
    .more-thumbnail {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      background: #ddd;
      font-size: 18px;
      font-weight: bold;
      color: #888;
    }
  }
  .meta-group.note {
    white-space: pre-wrap;
  }
}
.design-vendor-mapping {
  .vendor-mapping-inner {
    border: 1px dashed #ccc;
    padding: 10px 15px;
    border-radius: var(--border-radius, 0.25rem);
    min-width: 250px;
    &:empty {
      display: none;
    }
    .account-server {
      span {
        text-transform: capitalize;
      }
    }
  }
}
</style>
